import { useEffect, useState } from "react";
import { format, isToday, isBefore, startOfDay } from "date-fns";
import { Users, CalendarClock, AlertTriangle, PartyPopper, UtensilsCrossed, Target, ArrowRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useFeatures } from "@/context/FeatureContext";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-lg border bg-card p-4 card-shadow hover:-translate-y-0.5 transition-transform">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">{label}</span>
      <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
    </div>
    <p className="text-3xl font-bold mt-2">{value}</p>
  </div>
);

export function DashboardHome({ storeCode, onNavigate }) {
  const [data, setData] = useState(null);
  const { enabled } = useFeatures();

  useEffect(() => {
    const params = storeCode ? { store_code: storeCode } : {};
    Promise.all([
      api.get("/leads", { params }), api.get("/activities", { params }), api.get("/events", { params }),
    ]).then(([l, a, e]) => setData({ leads: l.data, activities: a.data, events: e.data })).catch(() => setData({ leads: [], activities: [], events: [] }));
  }, [storeCode]);

  if (!data) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-muted border-t-primary rounded-full animate-spin" /></div>;

  const today = startOfDay(new Date());
  const planned = data.activities.filter((a) => a.status !== "Completed");
  const overdue = planned.filter((a) => isBefore(new Date(a.scheduled_at), today));
  const todays = data.activities.filter((a) => isToday(new Date(a.scheduled_at)));
  const upcomingEvents = data.events.filter((e) => new Date(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);
  const cateringCount = data.activities.filter((a) => a.category === "catering").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-home">
      {enabled("dashboard_welcome_banner") && (
        <div data-testid="welcome-banner" className="rounded-lg bg-gradient-to-r from-primary to-pink-500 text-white px-5 py-4 flex items-center gap-3 card-shadow">
          <Sparkles className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Welcome to your marketing dashboard! 🎉</p>
            <p className="text-sm text-white/90">Track outreach, plan events, and grow your local community — all in one place.</p>
          </div>
        </div>
      )}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
        <p className="text-muted-foreground">Outreach overview{storeCode ? ` for store ${storeCode}` : ""}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="stat-leads" icon={Users} label="Targets / Leads" value={data.leads.length} accent />
        <Stat testid="stat-upcoming" icon={CalendarClock} label="Upcoming" value={planned.length} accent />
        <Stat testid="stat-overdue" icon={AlertTriangle} label="Overdue" value={overdue.length} />
        <Stat testid="stat-events" icon={PartyPopper} label="Events" value={data.events.length} />
        <Stat testid="stat-catering" icon={UtensilsCrossed} label="Catering" value={cateringCount} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="card-shadow border-0">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Today's Activities</CardTitle></CardHeader>
          <CardContent>
            {todays.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nothing scheduled today.</p> : (
              <div className="space-y-2">
                {todays.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2 rounded bg-secondary text-sm">
                    <span className="font-medium">{a.business_name}</span>
                    <span className="text-muted-foreground">{format(new Date(a.scheduled_at), "h:mm a")} · {a.status}</span>
                  </div>
                ))}
              </div>
            )}
            {overdue.length > 0 && (
              <button data-testid="home-overdue-link" onClick={() => onNavigate("calendar")} className="mt-3 w-full flex items-center justify-between p-2 rounded border border-destructive/30 bg-destructive/5 text-sm text-destructive">
                <span className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {overdue.length} overdue</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </CardContent>
        </Card>

        <Card className="card-shadow border-0">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PartyPopper className="h-4 w-4 text-primary" /> Upcoming Events</CardTitle></CardHeader>
          <CardContent>
            {upcomingEvents.length === 0 ? <p className="text-sm text-muted-foreground py-4">No upcoming events.</p> : (
              <div className="space-y-2">
                {upcomingEvents.map((e) => (
                  <div key={e.id} className="flex items-center justify-between p-2 rounded bg-secondary text-sm">
                    <span className="font-medium">{e.event_name}</span>
                    <span className="text-muted-foreground">{e.date}</span>
                  </div>
                ))}
              </div>
            )}
            <button data-testid="home-targets-link" onClick={() => onNavigate("contacts")} className="mt-3 w-full flex items-center justify-between p-2 rounded border bg-muted/40 text-sm hover:bg-muted">
              <span className="flex items-center gap-1.5"><Target className="h-4 w-4 text-primary" /> Manage targets & leads</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
